/**
 * Sensibilidad de scroll / inercia (localStorage).
 * Perfil de dispositivo (desktop / tablet / phone) define el default;
 * el usuario (lab/supremo) puede forzar un preset encima.
 */
import { isMobile, isTablet } from 'react-device-detect'

export const SCROLL_FEEL_STORAGE_KEY = 'udv.scrollFeel'
export const SCROLL_FEEL_EVENT = 'udv-scroll-feel-changed'

export const SCROLL_FEEL_PRESETS = ['precise', 'balanced', 'fluid']

const PRESET_TUNING = {
  precise: {
    wheelScale: 1.4,
    wheelLiveMs: 60,
    mprMinIntervalMs: 90,
    allowTouchInertia: false,
    inertiaDecay: 0.62,
    inertiaVelScale: 0.08,
    inertiaMaxSlicesCap: 2,
  },
  balanced: {
    wheelScale: 1,
    wheelLiveMs: 110,
    mprMinIntervalMs: 70,
    allowTouchInertia: true,
    inertiaDecay: 0.78,
    inertiaVelScale: 0.22,
    inertiaMaxSlicesCap: 4,
  },
  fluid: {
    wheelScale: 0.72,
    wheelLiveMs: 180,
    mprMinIntervalMs: 45,
    allowTouchInertia: true,
    inertiaDecay: 0.86,
    inertiaVelScale: 0.3,
    inertiaMaxSlicesCap: 8,
  },
}

function readDevice() {
  try {
    if (isTablet) return 'tablet'
    if (isMobile) return 'phone'
  } catch (e) {
    /* ignore */
  }
  return 'desktop'
}

export function detectScrollDevice() {
  return readDevice()
}

export function defaultScrollFeelPreset(device) {
  const d = device || readDevice()
  if (d === 'phone' || d === 'tablet') return 'balanced'
  return 'precise'
}

function normalizePreset(id) {
  const s = String(id || '').toLowerCase()
  if (SCROLL_FEEL_PRESETS.indexOf(s) >= 0) return s
  return null
}

export function getScrollFeelPrefs() {
  const device = readDevice()
  let preset = null
  try {
    const raw = localStorage.getItem(SCROLL_FEEL_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      preset = normalizePreset(parsed && parsed.preset)
    }
  } catch (e) {
    preset = null
  }
  return {
    device,
    preset: preset || defaultScrollFeelPreset(device),
    userSet: !!preset,
  }
}

export function setScrollFeelPreset(preset) {
  const next = normalizePreset(preset) || defaultScrollFeelPreset()
  try {
    localStorage.setItem(SCROLL_FEEL_STORAGE_KEY, JSON.stringify({ preset: next }))
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(SCROLL_FEEL_EVENT, { detail: { preset: next } }))
    }
  } catch (e2) {
    /* ignore */
  }
  return next
}

/**
 * @param {{ mpr?: boolean }} [opts]
 */
export function resolveScrollFeelTuning(opts = {}) {
  const prefs = getScrollFeelPrefs()
  const t = PRESET_TUNING[prefs.preset] || PRESET_TUNING.precise
  const mpr = !!opts.mpr
  return {
    ...t,
    device: prefs.device,
    preset: prefs.preset,
    wheelLiveMs: mpr ? Math.min(t.wheelLiveMs, 70) : t.wheelLiveMs,
    mprMinIntervalMs: mpr ? t.mprMinIntervalMs : 0,
    wheelScale: mpr ? Math.max(t.wheelScale, 1.25) : t.wheelScale,
  }
}
